import { html, nothing } from 'lit';
import { ago } from '../../shared/ui/format.js';
import { addOwner, removeOwner } from '../api/developer.js';
import { AccountView } from './account-view.js';

/**
 * Who runs the account.
 *
 * Owners manage members, tokens, billing and settings. An owner need not hold a seat, and an account
 * always keeps at least one - the API refuses to remove the last. Ownership goes only to an address
 * somebody has signed in with GitHub under.
 */
export class UserAccountOwners extends AccountView
{
   static section = 'members';

   static subpage = { id: 'owners', label: 'Owners' };

   static properties = { promoting: { state: true } };

   constructor()
   {
      super();
      this.promoting = false;
   }

   get pageActions()
   {
      return [{ id: 'add', label: 'Add an owner', icon: 'fa-solid fa-user-shield', variant: 'primary', disabled: this.busy }];
   }

   onPageAction(id)
   {
      if (id === 'add')
      {
         this.promoting = true;
      }
   }

   promote(event)
   {
      event.preventDefault();

      const address = String(new FormData(event.target).get('email') ?? '').trim();

      if (!address)
      {
         return;
      }

      this.promoting = false;

      void this.act(() => addOwner(this.account, address), `${address} now owns this team too.`);
   }

   get body()
   {
      const owners = this.team.owners ?? [];

      // The table is not inside a card: with nothing to search it hands a card an empty header strip,
      // and on its own it draws a card without one.
      return html`
         ${this.promoting
            ? html`
               <lia-card title="Add an owner" icon="fa-solid fa-user-shield">
                  <form class="d-flex flex-wrap gap-2" @submit=${this.promote}>
                     <input
                        class="form-control form-control-sm flex-grow-1 covdbg-address"
                        type="email"
                        name="email"
                        placeholder="An address that has signed in with GitHub"
                        aria-label="Address of the new owner"
                        required
                        autofocus
                        ?disabled=${this.busy}
                     >
                     <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this.busy}>Add as owner</button>
                     <button class="btn btn-sm btn-link" type="button" @click=${() => { this.promoting = false; }}>
                        Cancel
                     </button>
                  </form>
               </lia-card>`
            : nothing}

         <div>
            <lia-data-table
               listing-id="account-owners"
               .perPage=${10}
               no-search
               no-column-manager
               ?loading=${this.busy}
               .columns=${[
                  {
                     key: 'displayName',
                     label: 'Owner',
                     sortable: true,
                     render: row => html`
                        ${row.displayName || row.email || row.principalId}
                        ${row.you ? html`<span class="badge text-bg-secondary ms-1">You</span>` : nothing}
                        ${row.email && row.email !== row.displayName
                           ? html`<div class="small text-body-secondary">${row.email}</div>`
                           : nothing}`
                  },
                  { key: 'since', label: 'Since', sortable: true, render: row => ago(row.since) },
                  { key: 'addedBy', label: 'Added by', sortable: true, render: row => row.addedBy || '—' }
               ]}
               .rows=${owners}
               .rowKey=${row => row.principalId}
               .rowActions=${row => (owners.length > 1
                  ? [{
                     id: 'remove',
                     icon: 'fa-solid fa-user-minus',
                     title: row.you ? 'Step down' : 'Remove as owner',
                     variant: 'outline-danger',
                     size: 'sm',
                     disabled: this.busy,
                     confirm: {
                        title: row.you ? 'Step down as owner?' : 'Remove as owner?',
                        message: row.you
                           ? 'You keep any seat you hold and stop being able to manage this team.'
                           : `${row.email ?? 'They'} keep any seat they hold and stop being able to manage this team.`,
                        confirmLabel: row.you ? 'Step down' : 'Remove',
                        variant: 'danger'
                     }
                  }]
                  : [])}
               @lia-action=${event =>
               {
                  const row = event.detail?.row;

                  if (row)
                  {
                     void this.act(
                        () => removeOwner(this.account, row.principalId),
                        row.you ? 'You no longer own this team.' : `${row.email ?? 'They'} no longer own this team.`);
                  }
               }}
            ></lia-data-table>
         </div>`;
   }
}

customElements.define('user-account-owners', UserAccountOwners);
